import { ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { useAgents } from '../hooks/useAgents';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAgentApi, updateEventsFunctionApi } from '../utils/apis';
import {
  IUpdateAssistant,
  IUpdateEventsFunction,
  SystemPromptData,
} from '../schemas/apis/interface';
import { useParams } from 'react-router-dom';

interface AgentConfig {
  agentId: string;
  title: string;
  description: string;
  urlId: string;
}

export interface AgentFileConfig {
  constraints: string;
  context: string;
  examples: string;
  firstMessage: string;
  format: string;
  identity: string;
  job: string;
  label?: string[];
  functionconfig: {
    webhook: [];
    webform: [];
  };
  isMultilingual?: boolean;
  multi_voice?: {
    language: { name: string; value: string };
    stt: string;
    tts: string;
    voice: { name: string; value: string };
  }[];
  whatsAppDetails?: {
    model?: string;
    historyResponse?: number;
    debugMode?: boolean;
    whatsappPhonenumberId?: string;
    whatsappToken?: string;
    whatsappVerificationToken?: string;
  };
  languageCapability: string;
  tone: string;
  version?: number;
  versionId?: string;
}

export interface Agent {
  agentConfig: AgentConfig;
  agentKey: string;
  agentName: string;
  agentChannel: 'whatsapp' | 'voice';
  application: null;
  config: {
    voice: {
      voiceId: string;
      name: string;
      voiceName: string;
      provider: string;
      _id: string;
    };
    xiLabsConfig: {
      latency: number;
      stability: number;
      speed: number;
      similarity: number;
    };
    variables: {
      key: string;
      value: string;
    }[];
    language: { name: string; value: string };
    noiseSuppression: boolean;
    userTimeout: number;
    warmTransfer: boolean;
    languageCapability: string;
    _id: string;
    tts: string;
    llm: string;
    stt: string;
    timeZone: string;
    responseDelay: number;
    events: {
      eventName: string;
      active: boolean;
      _id: string;
      eventValue?: string;
    }[];
    knowledgeBase: {
      store: {
        id: number;
        friendly_name: string;
      };
    }[];
    isMultilingual?: boolean;
    multi_voice?: {
      language: { name: string; value: string };
      stt: string;
      tts: string;
      voice: { name: string; value: string };
      _id: string;
    }[];
    whatsAppDetails?: {
      model?: string;
      historyResponse?: number;
      debugMode?: boolean;
      whatsappPhonenumberId?: string;
      whatsappToken?: string;
      whatsappVerificationToken?: string;
    };
    whatsappConfig?: {
      model?: string;
      historyResponse?: number;
      debugMode?: boolean;
      whatsappPhonenumberId?: string;
      whatsappToken?: string;
      whatsappVerificationToken?: string;
      linkedVoiceAgentId?: string;
    };
    vad?: {
      turnEndpointDelay: string;
      minimumTurnDuration: string;
      minimumInterruptionDuration: string;
      frameActivationThreshold: number;
    };
    tools?: { toolName: string; toolId: string }[];
    linkedWhatsappAgent?: {
      agentId: string;
      whatsappPhonenumberId: string;
      whatsappVerificationToken: string;
      whatsappToken: string;
    };
  };
  assignedNumbers: Record<string, string | string[]>;

  functionconfig: {
    webhook: [];
    webform: [];
  };
  deleted: boolean;
  file: AgentFileConfig;
  templateId?: string;
  phoneAccountType?: string;
  _id: string;
}

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const [selectedAssistant, setSelectedAssistant] = useState<Agent | null>(
    null
  );
  const { agents, isLoading } = useAgents();
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null
  );
  const queryClient = useQueryClient();
  const [localValues, setLocalValues] = useState<SystemPromptData>({
    identity: '',
    firstMessage: '',
    context: '',
    job: '',
    examples: '',
    constraints: '',
    format: '',
    tone: '',
  });
  const { agentName } = useParams<{ agentName: string }>();

  useEffect(() => {
    if (selectedAssistant?.file) {
      setLocalValues({
        constraints: selectedAssistant.file.constraints || '',
        context: selectedAssistant.file.context || '',
        examples: selectedAssistant.file.examples || '',
        format: selectedAssistant.file.format || '',
        tone: selectedAssistant.file.tone || '',
        identity: selectedAssistant.file.identity || '',
        job: selectedAssistant.file.job || '',
        firstMessage: selectedAssistant.file.firstMessage || '',
      });
    }
  }, [selectedAssistant, selectedAssistantId]);

  // Only auto-select agent when inside a route with :agentName param
  // (i.e., inside the inner DashboardProvider in Dashboard.tsx).
  // The outer provider (above Routes) always has agentName=undefined, so this is a no-op there.
  // Clearing selectedAssistant when navigating away is handled by the sidebar's useEffect.
  useEffect(() => {
    if (!isLoading && agents?.agents && agentName) {
      const agent = agents.agents.find(
        (a: Agent) =>
          a.agentName?.toLowerCase().replace(/\s+/g, '-') ===
          decodeURIComponent(agentName)
      );
      if (agent) {
        setSelectedAssistant(agent);
        setSelectedAssistantId(agent._id);
      }
    }
  }, [agents, isLoading, agentName]);

  const updateAssistantEvent = useCallback(
    (eventName: string, eventValue: string, active: boolean) => {
      setSelectedAssistant(prevState => {
        if (!prevState) return null;
        const updatedEvents = prevState.config.events.map(event =>
          event.eventName === eventName
            ? {
                ...event,
                active,
                eventValue: active ? eventValue : undefined,
                _id: event._id,
              }
            : event
        );
        return {
          ...prevState,
          config: { ...prevState.config, events: updatedEvents },
        };
      });
    },
    []
  );

  const {
    mutateAsync: updateEventFunction,
    isPending: isUpdateEventFunctionPending,
  } = useMutation({
    mutationFn: async (values: IUpdateEventsFunction) => {
      await updateEventsFunctionApi(values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: error => {
      console.error('Error updating event function:', error);
    },
  });

  const {
    mutateAsync: updateAssistantSystemPrompt,
    isPending: isUpdateSystemPromptPending,
  } = useMutation({
    mutationFn: async (values: IUpdateAssistant) => {
      await updateAgentApi(values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agents', 'get-version-history'],
      });
    },
    onError: (error: any) => {
      console.log('Error while updating system prompt', error);
    },
  });

  const handleInputChange = useCallback(
    (key: keyof SystemPromptData, value: string) => {
      setLocalValues((prev: SystemPromptData) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      selectedAssistant,
      setSelectedAssistant,
      selectedAssistantId,
      setSelectedAssistantId,
      updateAssistantEvent,
      updateEventFunction,
      isUpdateEventFunctionPending,
      updateAssistantSystemPrompt,
      isUpdateSystemPromptPending,
      localValues,
      setLocalValues,
      handleInputChange,
    }),
    [
      selectedAssistant,
      selectedAssistantId,
      updateAssistantEvent,
      updateEventFunction,
      isUpdateEventFunctionPending,
      updateAssistantSystemPrompt,
      isUpdateSystemPromptPending,
      localValues,
      handleInputChange,
    ]
  );

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};
